/*
  This file contains the logic for parsing the event log data from the BPR DU.
  It is used to parse the event log data from the BPR DU and return the parsed data in a human-readable format.
  Logic copied from bpr.py in case future updates are needed, they might already be working there.
*/

const EVENT_LOG_ENCODING_BYTE_EOP = 0xff;
const EVENT_LOG_ENCODING_BYTE_ESC = 0xfe;
const EVENT_LOG_ENCODING_BYTE_ESC_EOP = 0x77;
const EVENT_LOG_ENCODING_BYTE_ESC_ESC = 0x76;

const APPLICATION_SPECIFIC_EVENT_FLAG = 0x80;

enum CoreLogEventTypesEnum {
  CATASTROPHIC_ERROR = 0,
  WATCHDOG_RESET = 1,
  HARDWARE_RESET = 2,
}

enum FirmwareTypesEnum {
  BOOTLOADER = 0,
  MAIN_APP = 1,
  RADIO = 2,
  LAUNCHER = 3,
  COPROCESSOR = 4,
}

enum BootReasonsEnum {
  COLD_BOOT = 0,
  UNEXPECTED_RESET = 1,
  CATASTROPHIC_ERROR = 2,
  FIRMWARE_UPDATE = 3,
  PROCESSOR_SLEEP = 4,
  PROCESSOR_RESET = 5,
  UNKNOWN = 6,
  PIN_RESET = 7,
  WATCHDOG_TIMER = 8,
  CPU_LOCKUP = 9,
  PROCESSOR_SPECIFIC = 10,
  CPU_WAKE_FROM_OFF = 11,
  FINALIZE_FIRMWARE_UPDATE = 12,
  COPROCESSOR_CPU_LOCKUP = 13,
}

function titleFromEnumName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatDataHex(data: Uint8Array): string {
  if (data.length === 0) {
    return 'None';
  }
  return Array.from(data, (b) => b.toString(16).padStart(2, '0'))
    .join(' ')
    .toUpperCase();
}

class StructUnpacker {
  private offset = 0;
  private readonly view: DataView;

  constructor(private readonly data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  unpackUint8(): number {
    const v = this.data[this.offset];
    this.offset += 1;
    return v;
  }

  unpackUint16(): number {
    const v = this.view.getUint16(this.offset, false);
    this.offset += 2;
    return v;
  }

  unpackString(): string {
    const start = this.offset;
    let nullPos = -1;
    for (let i = start; i < this.data.length; i++) {
      if (this.data[i] === 0) {
        nullPos = i;
        break;
      }
    }
    const end = nullPos === -1 ? this.data.length : nullPos;
    const slice = this.data.subarray(start, end);
    this.offset = nullPos === -1 ? this.data.length : nullPos + 1;
    return new TextDecoder('utf-8', { fatal: false }).decode(slice);
  }

  unpackBytesRest(): Uint8Array {
    const rest = this.data.subarray(this.offset);
    this.offset = this.data.length;
    return rest;
  }
}

export interface ParsedLogEntry {
  readonly typeName: string;
  readonly infoString: string;
}

abstract class LogEntry implements ParsedLogEntry {
  abstract get typeName(): string;
  abstract get infoString(): string;
}

function numericEnumValues(e: Record<string, number | string>): number[] {
  return Object.values(e).filter((v): v is number => typeof v === 'number');
}

function isFirmwareTypesEnumValue(v: number): v is FirmwareTypesEnum {
  return numericEnumValues(FirmwareTypesEnum).includes(v);
}

function isBootReasonsEnumValue(v: number): v is BootReasonsEnum {
  return numericEnumValues(BootReasonsEnum).includes(v);
}

class CatastrophicError extends LogEntry {
  readonly firmwareType: FirmwareTypesEnum;
  readonly majorVersion: number;
  readonly minorVersion: number;
  readonly buildNumber: number;
  readonly filename: string;
  readonly lineNumber: number;
  readonly errorData: Uint8Array;

  constructor(eventData: Uint8Array) {
    super();
    const unpacker = new StructUnpacker(eventData);
    const ft = unpacker.unpackUint8();
    if (!isFirmwareTypesEnumValue(ft)) {
      throw new RangeError(`invalid FirmwareTypesEnum: ${ft}`);
    }
    this.firmwareType = ft;
    this.majorVersion = unpacker.unpackUint16();
    this.minorVersion = unpacker.unpackUint16();
    this.buildNumber = unpacker.unpackUint16();
    this.filename = unpacker.unpackString();
    this.lineNumber = unpacker.unpackUint16();
    this.errorData = unpacker.unpackBytesRest();
  }

  get typeName(): string {
    return 'Catastrophic Error';
  }

  get infoString(): string {
    const fwName = FirmwareTypesEnum[this.firmwareType] as string;
    const fw = titleFromEnumName(fwName);
    const versionString = `v${this.majorVersion}.${this.minorVersion}.${this.buildNumber}`;
    const dataStr = formatDataHex(this.errorData);
    return `App: ${fw} ${versionString} - File: ${this.filename}:${this.lineNumber} - Data: ${dataStr}`;
  }
}

class WatchdogReset extends LogEntry {
  get typeName(): string {
    return 'Watchdog Reset';
  }

  get infoString(): string {
    return '';
  }
}

class HardwareReset extends LogEntry {
  readonly bootReason: BootReasonsEnum;

  constructor(eventData: Uint8Array) {
    super();
    if (eventData.length < 1) {
      throw new RangeError('hardware reset payload too short');
    }
    const code = eventData[0];
    if (!isBootReasonsEnumValue(code)) {
      throw new RangeError(`invalid BootReasonsEnum: ${code}`);
    }
    this.bootReason = code;
  }

  get typeName(): string {
    return 'Hardware Reset';
  }

  get infoString(): string {
    const label = titleFromEnumName(BootReasonsEnum[this.bootReason]);
    return `Boot Reason: ${label}`;
  }
}

class ApplicationSpecificEvent extends LogEntry {
  readonly type: number;
  readonly eventData: Uint8Array;

  constructor(eventType: number, eventData: Uint8Array) {
    super();
    this.type = eventType & 0x7f;
    this.eventData = eventData;
  }

  get typeName(): string {
    return `Application Specific Event (0x${this.type.toString(16).padStart(2, '0').toUpperCase()})`;
  }

  get infoString(): string {
    return `Data: ${formatDataHex(this.eventData)}`;
  }
}

class UnknownSystemEvent extends LogEntry {
  readonly type: number;
  readonly eventData: Uint8Array;

  constructor(eventType: number, eventData: Uint8Array) {
    super();
    this.type = eventType;
    this.eventData = eventData;
  }

  get typeName(): string {
    return `Unknown System Event (0x${this.type.toString(16).padStart(2, '0').toUpperCase()})`;
  }

  get infoString(): string {
    return `Data: ${formatDataHex(this.eventData)}`;
  }
}

class MalformedEvent extends LogEntry {
  readonly rawBytes: Uint8Array;

  constructor(rawBytes: Uint8Array) {
    super();
    this.rawBytes = rawBytes;
  }

  get typeName(): string {
    return 'Malformed Event';
  }

  get infoString(): string {
    return `${this.rawBytes.length} bytes`;
  }
}

function decodeByteStream(encodedBytes: Uint8Array): Uint8Array | null {
  const decoded: number[] = [];
  let i = 0;
  while (i < encodedBytes.length) {
    if (encodedBytes[i] === EVENT_LOG_ENCODING_BYTE_ESC) {
      if (i + 1 >= encodedBytes.length) {
        return null;
      }
      const nextByte = encodedBytes[i + 1];
      if (nextByte === EVENT_LOG_ENCODING_BYTE_ESC_EOP) {
        decoded.push(EVENT_LOG_ENCODING_BYTE_EOP);
        i += 2;
      } else if (nextByte === EVENT_LOG_ENCODING_BYTE_ESC_ESC) {
        decoded.push(EVENT_LOG_ENCODING_BYTE_ESC);
        i += 2;
      } else {
        return null;
      }
    } else {
      decoded.push(encodedBytes[i]);
      i += 1;
    }
  }
  return Uint8Array.from(decoded);
}

function isTextControlByte(byte: number): boolean {
  return byte === 0 || byte === 0x09 || byte === 0x0a || byte === 0x0d;
}

export function looksLikeBinaryEventLog(rawBytes: Uint8Array): boolean {
  return (
    (rawBytes.length > 0 && (rawBytes[0] & APPLICATION_SPECIFIC_EVENT_FLAG) !== 0) ||
    rawBytes.some((byte) => byte === EVENT_LOG_ENCODING_BYTE_EOP || byte === EVENT_LOG_ENCODING_BYTE_ESC) ||
    rawBytes.some((byte) => !isTextControlByte(byte) && (byte < 0x20 || byte === 0x7f))
  );
}

function isChecksumValid(decodedEventBytes: Uint8Array): boolean {
  const checksumByte = decodedEventBytes[decodedEventBytes.length - 1];
  let calculated = 0;
  for (let j = 0; j < decodedEventBytes.length - 1; j++) {
    calculated += decodedEventBytes[j];
  }
  calculated = (calculated & 0xff) ^ 0xff;
  return calculated === checksumByte;
}

function parseSingleEvent(encodedEventBytes: Uint8Array): LogEntry {
  const decodedEventBytes = decodeByteStream(encodedEventBytes);
  if (decodedEventBytes == null || decodedEventBytes.length < 2) {
    return new MalformedEvent(encodedEventBytes);
  }
  const eventType = decodedEventBytes[0];
  const eventData = decodedEventBytes.subarray(1, decodedEventBytes.length - 1);

  if (!isChecksumValid(decodedEventBytes)) {
    return new MalformedEvent(encodedEventBytes);
  }

  try {
    switch (eventType) {
      case CoreLogEventTypesEnum.CATASTROPHIC_ERROR:
        return new CatastrophicError(eventData);
      case CoreLogEventTypesEnum.WATCHDOG_RESET:
        return new WatchdogReset();
      case CoreLogEventTypesEnum.HARDWARE_RESET:
        return new HardwareReset(eventData);
      default:
        if ((eventType & APPLICATION_SPECIFIC_EVENT_FLAG) !== 0) {
          return new ApplicationSpecificEvent(eventType, eventData);
        }
        return new UnknownSystemEvent(eventType, eventData);
    }
  } catch {
    return new MalformedEvent(encodedEventBytes);
  }
}

export function isMalformedLogEntry(entry: ParsedLogEntry): boolean {
  return entry instanceof MalformedEvent;
}

export function parseEventLog(rawBytes: Uint8Array): ParsedLogEntry[] {
  if (rawBytes.length === 0) {
    return [];
  }

  const entries: ParsedLogEntry[] = [];
  let eventStart = 0;

  for (let i = 0; i < rawBytes.length; i++) {
    if (rawBytes[i] === EVENT_LOG_ENCODING_BYTE_EOP) {
      if (i > eventStart) {
        const entry = parseSingleEvent(rawBytes.subarray(eventStart, i));
        entries.push(entry);
      }
      eventStart = i + 1;
    }
  }

  if (eventStart < rawBytes.length) {
    const entry = parseSingleEvent(rawBytes.subarray(eventStart));
    entries.push(entry);
  }

  return entries;
}

export interface LogTableRow {
  index: number;
  type: string;
  details: string;
}

export function logEntriesToRows(entries: readonly ParsedLogEntry[]): LogTableRow[] {
  return entries.map((entry, index) => ({
    index,
    type: entry.typeName,
    details: entry.infoString,
  }));
}
